import React, { useState, useEffect } from "react";
import { Breadcrumb, Layout, Steps, Card, Button, message, theme, Spin, Alert } from "antd";
import { useAuth } from "../hooks/useAuth";
import { useIntl } from "react-intl";
import { useParams } from "react-router-dom";
import { useScopedNavigate } from "../components/MarketerView/useScopedNavigate";
import { ArrowLeftOutlined, SaveOutlined } from "@ant-design/icons";
import { useGetBrandsQuery, useGetSurveysQuery, useUpdateSurveyMutation, useGetSurveyCohortsQuery } from "../services/nestjs-base-api";
import { ISurveyTheme, IBrand, ISurvey } from "../services/nestjs-base-api/types";
import BasicInfoStep from "../components/Survey/BasicInfoStep";
import ConfigurationStep from "../components/Survey/ConfigurationStep";
import ThemeStep from "../components/Survey/ThemeStep";

const { Content } = Layout;

interface SurveyFormData {
    name: string;
    brandId: number;
    cohortId: number;
    countryCode: string;
    languageCode: string;
    description?: string;
    startsAt?: Date;
    endsAt?: Date;
    questionsPerResponse: number;
    rotationStrategy: 'sequential' | 'random' | 'balanced' | 'adaptive';
    theme: ISurveyTheme;
    showProgressBar: boolean;
    animationSpeed: number;
    allowSkip: boolean;
    allowBackNavigation: boolean;
    persistProgress: boolean;
    expirationHours: number;
    completionTitle: string;
    completionSubtitle: string;
    completionButtonText: string;
    completionButtonLink?: string;
    showCompletionButton: boolean;
    completionSecondaryButtonText?: string;
    completionSecondaryButtonLink?: string;
    showReward?: boolean;
    discountCode?: string;
    offerDescription?: string;
    offerTerms?: string;
    showCompletionSecondaryButton: boolean;
    collectEmail: boolean;
    emailRequired: boolean;
    emailPlaceholder: string;
    npsRequired: boolean;
    showWelcomePage: boolean;
    finalQuestionId?: number | null;
}

const EditSurveyPage: React.FunctionComponent = () => {
    const { user } = useAuth();
    const intl = useIntl();
    const navigate = useScopedNavigate();
    const { surveyHash } = useParams<{ surveyHash: string }>();
    const {
        token: { colorBgContainer, borderRadiusLG },
    } = theme.useToken();

    const topOfWizard = 170;

    const [currentStep, setCurrentStep] = useState(0);
    const [formData, setFormData] = useState<SurveyFormData>({
        name: '',
        brandId: 0,
        cohortId: 0,
        countryCode: 'AU',
        languageCode: 'en',
        description: '',
        questionsPerResponse: 12,
        rotationStrategy: 'balanced',
        theme: {
            primaryColor: '#4338ca',
            secondaryColor: '#1e40af',
            backgroundColor: '#1e293b',
            cardColor: '#334155',
            accentColor: '#3b82f6'
        },
        showProgressBar: true,
        animationSpeed: 800,
        allowSkip: false,
        allowBackNavigation: true,
        persistProgress: true,
        expirationHours: 24,
        completionTitle: 'Survey Complete!',
        completionSubtitle: 'Thank you for taking the time to share your feedback...',
        completionButtonText: 'Visit our website',
        completionButtonLink: '',
        showCompletionButton: false,
        completionSecondaryButtonText: 'Leave us a review',
        completionSecondaryButtonLink: '',
        showReward: false,
        discountCode: '',
        offerDescription: '',
        offerTerms: '',
        showCompletionSecondaryButton: false,
        collectEmail: true,
        emailRequired: true,
        emailPlaceholder: 'Enter your email address',
        npsRequired: true,
        showWelcomePage: true,
        finalQuestionId: null
    });

    const [errors, setErrors] = useState<Record<string, string>>({});
    const [isInitialized, setIsInitialized] = useState(false);

    // Fetch surveys to find the one being edited
    const { data: surveys, isLoading: surveysLoading } = useGetSurveysQuery({
        jwtToken: user?.jwtToken!,
        organisationId: user?.organisation?.id!
    }, {
        skip: !user?.jwtToken || !user?.organisation?.id
    });

    // Fetch brands for dropdown
    const { data: brands, isLoading: brandsLoading } = useGetBrandsQuery({
        jwtToken: user?.jwtToken!,
        organisationId: user?.organisation?.id!
    }, {
        skip: !user?.jwtToken || !user?.organisation?.id
    });

    // Fetch cohorts for dropdown
    const { data: cohorts, isLoading: cohortsLoading } = useGetSurveyCohortsQuery({
        jwtToken: user?.jwtToken!,
        organisationId: user?.organisation?.id!
    }, {
        skip: !user?.jwtToken || !user?.organisation?.id
    });

    const [updateSurvey, { isLoading: isSubmitting }] = useUpdateSurveyMutation();

    const survey = surveys?.find((s: ISurvey) => s.hash === surveyHash);

    // Initialize form data with existing survey data
    useEffect(() => {
        if (survey && !isInitialized) {
            const settings = survey.settings;
            
            // Parse theme JSON string
            let parsedTheme: ISurveyTheme = {
                primaryColor: '#4338ca',
                secondaryColor: '#1e40af',
                backgroundColor: '#1e293b',
                cardColor: '#334155',
                accentColor: '#3b82f6'
            };
            
            if (settings?.theme) {
                try {
                    parsedTheme = JSON.parse(settings.theme);
                } catch (error) {
                    console.warn('Failed to parse theme JSON:', error);
                }
            }
            
            setFormData({
                name: survey.name,
                brandId: survey.brand?.id || 0,
                cohortId: survey.cohort?.id || 0,
                countryCode: survey.countryCode || 'AU',
                languageCode: survey.languageCode || 'en',
                description: survey.description,
                startsAt: survey.startsAt ? new Date(survey.startsAt) : undefined,
                endsAt: survey.endsAt ? new Date(survey.endsAt) : undefined,
                questionsPerResponse: settings?.questionsPerResponse || 12,
                rotationStrategy: settings?.rotationStrategy || 'balanced',
                theme: parsedTheme,
                showProgressBar: settings?.showProgressBar ?? true,
                animationSpeed: settings?.animationSpeed ?? 800,
                allowSkip: settings?.allowSkip ?? false,
                allowBackNavigation: settings?.allowBackNavigation ?? true,
                persistProgress: settings?.persistProgress ?? true,
                expirationHours: settings?.expirationHours ?? 24,
                completionTitle: settings?.completionTitle ?? 'Survey Complete!',
                completionSubtitle: settings?.completionSubtitle ?? 'Thank you for taking the time to share your feedback...',
                completionButtonText: settings?.completionButtonText ?? 'Visit our website',
                completionButtonLink: settings?.completionButtonLink ?? '',
                showCompletionButton: settings?.showCompletionButton ?? false,
                completionSecondaryButtonText: settings?.completionSecondaryButtonText ?? 'Leave us a review',
                completionSecondaryButtonLink: settings?.completionSecondaryButtonLink ?? '',
                showReward: settings?.showReward ?? false,
                discountCode: settings?.discountCode ?? '',
                offerDescription: settings?.offerDescription ?? '',
                offerTerms: settings?.offerTerms ?? '',
                showCompletionSecondaryButton: settings?.showCompletionSecondaryButton ?? false,
                collectEmail: settings?.collectEmail ?? true,
                emailRequired: settings?.emailRequired ?? true,
                emailPlaceholder: settings?.emailPlaceholder ?? 'Enter your email address',
                npsRequired: settings?.npsRequired ?? true,
                showWelcomePage: settings?.showWelcomePage ?? true,
                finalQuestionId: settings?.finalQuestionId ?? null
            });
            setIsInitialized(true);
        }
    }, [survey, isInitialized]);

    // Update theme colors when brand is selected
    // Only update if the brand has actually changed (not on initial load)
    useEffect(() => {
        if (formData.brandId && brands && isInitialized && survey) {
            // Check if the brand ID has actually changed from the original
            if (formData.brandId !== survey.brand?.id) {
                const selectedBrand = brands.find((brand: IBrand) => brand.id === formData.brandId);
                if (selectedBrand) {
                    setFormData(prev => ({
                        ...prev,
                        theme: {
                            ...prev.theme,
                            primaryColor: selectedBrand.primaryColor || '#4338ca'
                        }
                    }));
                }
            }
        }
    }, [formData.brandId, brands, isInitialized, survey]);

    const steps = [
        {
            title: intl.formatMessage({ id: 'survey.edit.step1.title' }),
            description: intl.formatMessage({ id: 'survey.edit.step1.description' })
        },
        {
            title: intl.formatMessage({ id: 'survey.edit.step2.title' }),
            description: intl.formatMessage({ id: 'survey.edit.step2.description' })
        },
        {
            title: intl.formatMessage({ id: 'survey.edit.step3.title' }),
            description: intl.formatMessage({ id: 'survey.edit.step3.description' })
        }
    ];

    const validateStep = (step: number): boolean => {
        const newErrors: Record<string, string> = {};

        if (step === 0) {
            if (!formData.name || formData.name.length < 3) {
                newErrors.name = intl.formatMessage({ id: 'survey.form.validation.nameRequired' });
            }
            if (!formData.brandId) {
                newErrors.brandId = intl.formatMessage({ id: 'survey.form.validation.brandRequired' });
            }
            if (!formData.cohortId) {
                newErrors.cohortId = 'Please select a customer cohort';
            }
            // For editing, we allow changing cohort even if active surveys exist
            // since the current survey would be excluded from the check
        }

        if (step === 1) {
            if (formData.startsAt && formData.endsAt && formData.startsAt >= formData.endsAt) {
                newErrors.endsAt = intl.formatMessage({ id: 'survey.form.validation.endDateAfterStart' });
            }
            if (formData.questionsPerResponse < 5 || formData.questionsPerResponse > 15) {
                newErrors.questionsPerResponse = intl.formatMessage({ id: 'survey.form.validation.questionsRange' });
            }
            if (formData.animationSpeed < 200 || formData.animationSpeed > 2000) {
                newErrors.animationSpeed = intl.formatMessage({ id: 'survey.form.validation.animationSpeedRange' });
            }
            if (formData.expirationHours < 1 || formData.expirationHours > 168) {
                newErrors.expirationHours = intl.formatMessage({ id: 'survey.form.validation.expirationHoursRange' });
            }
            // Primary CTA: both fields required when shown
            if (formData.showCompletionButton) {
                if (!formData.completionButtonText?.trim()) {
                    newErrors.completionButtonText = intl.formatMessage({ id: 'survey.form.validation.buttonTextRequired' });
                }
                if (!formData.completionButtonLink?.trim()) {
                    newErrors.completionButtonLink = intl.formatMessage({ id: 'survey.form.validation.buttonLinkRequired' });
                }
            }
            if (formData.completionButtonLink && formData.completionButtonLink.trim()) {
                try {
                    new URL(formData.completionButtonLink);
                } catch {
                    newErrors.completionButtonLink = intl.formatMessage({ id: 'survey.form.validation.invalidUrl' });
                }
            }
            // Secondary CTA (SYR-93): both fields required when shown, link must be https
            if (formData.showCompletionSecondaryButton) {
                if (!formData.completionSecondaryButtonText?.trim()) {
                    newErrors.completionSecondaryButtonText = intl.formatMessage({ id: 'survey.form.validation.secondaryButtonTextRequired' });
                }
                if (!formData.completionSecondaryButtonLink?.trim()) {
                    newErrors.completionSecondaryButtonLink = intl.formatMessage({ id: 'survey.form.validation.secondaryButtonLinkRequired' });
                }
            }
            if (formData.completionSecondaryButtonLink && formData.completionSecondaryButtonLink.trim()) {
                let secondaryLinkValid = false;
                try {
                    secondaryLinkValid = new URL(formData.completionSecondaryButtonLink).protocol === 'https:';
                } catch {
                    secondaryLinkValid = false;
                }
                if (!secondaryLinkValid) {
                    newErrors.completionSecondaryButtonLink = intl.formatMessage({ id: 'survey.form.validation.httpsUrlRequired' });
                }
            }
            if (formData.emailPlaceholder && formData.emailPlaceholder.length > 255) {
                newErrors.emailPlaceholder = intl.formatMessage({ id: 'survey.form.validation.emailPlaceholderLength' });
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Smooth-scroll to the first invalid field so a blocked Next is never silent.
    // Deferred so the validation re-render has painted the error state first.
    const scrollToFirstError = () => {
        setTimeout(() => {
            const el = document.querySelector(
                '.ant-form-item-has-error, .ant-input-status-error, .ant-input-number-status-error, .ant-picker-status-error, .ant-select-status-error'
            );
            el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 100);
    };

    const handleNext = () => {
        if (validateStep(currentStep)) {
            setCurrentStep(currentStep + 1);
            // Scroll to top to show progress indicator
            window.scrollTo({ top: topOfWizard, behavior: 'smooth' });
        } else {
            scrollToFirstError();
        }
    };

    const handlePrevious = () => {
        setCurrentStep(currentStep - 1);
        // Scroll to top to show progress indicator
        window.scrollTo({ top: topOfWizard, behavior: 'smooth' });
    };

    const handleSubmit = async () => {
        if (!validateStep(currentStep)) {
            scrollToFirstError();
            return;
        }
        if (!survey) return;

        try {
            const payload = {
                name: formData.name,
                brandId: formData.brandId,
                cohortId: formData.cohortId,
                countryCode: formData.countryCode || 'AU',
                languageCode: formData.languageCode || 'en',
                description: formData.description || undefined,
                startsAt: formData.startsAt ? formData.startsAt.toISOString() : undefined,
                endsAt: formData.endsAt ? formData.endsAt.toISOString() : undefined,
                questionsPerResponse: formData.questionsPerResponse,
                rotationStrategy: formData.rotationStrategy,
                theme: formData.theme,
                showProgressBar: formData.showProgressBar,
                animationSpeed: formData.animationSpeed,
                allowSkip: formData.allowSkip,
                allowBackNavigation: formData.allowBackNavigation,
                persistProgress: formData.persistProgress,
                expirationHours: formData.expirationHours,
                completionTitle: formData.completionTitle,
                completionSubtitle: formData.completionSubtitle,
                completionButtonText: formData.completionButtonText,
                completionButtonLink: formData.completionButtonLink || undefined,
                showCompletionButton: formData.showCompletionButton,
                completionSecondaryButtonText: formData.completionSecondaryButtonText || undefined,
                completionSecondaryButtonLink: formData.completionSecondaryButtonLink || undefined,
                showReward: formData.showReward ?? false,
                discountCode: formData.discountCode || null,
                offerDescription: formData.offerDescription || null,
                offerTerms: formData.offerTerms || null,
                showCompletionSecondaryButton: formData.showCompletionSecondaryButton,
                collectEmail: true,
                emailRequired: true,
                emailPlaceholder: formData.emailPlaceholder,
                npsRequired: formData.npsRequired,
                showWelcomePage: formData.showWelcomePage,
                finalQuestionId: formData.finalQuestionId
            };

            await updateSurvey({
                jwtToken: user?.jwtToken!,
                surveyId: survey.id,
                payload
            }).unwrap();

            message.success(intl.formatMessage({ id: 'survey.edit.success' }, { name: formData.name }));
            navigate(`/dashboard/surveys/${surveyHash}`);
        } catch (error: any) {
            console.error('Failed to update survey:', error);
            // Show backend error message if available (e.g., cohort+country conflict)
            const errorMessage = error?.data?.message || intl.formatMessage({ id: 'survey.edit.error' });
            message.error(errorMessage);
        }
    };

    const updateFormData = (updates: Partial<SurveyFormData>) => {
        setFormData(prev => ({ ...prev, ...updates }));
    };

    const renderStepContent = () => {
        switch (currentStep) {
            case 0:
                return (
                    <BasicInfoStep
                        formData={formData}
                        brands={brands || []}
                        brandsLoading={brandsLoading}
                        cohorts={cohorts || []}
                        cohortsLoading={cohortsLoading}
                        errors={errors}
                        onChange={updateFormData}
                        isEditMode={true}
                    />
                );
            case 1:
                return (
                    <ConfigurationStep
                        formData={formData}
                        errors={errors}
                        onChange={updateFormData}
                    />
                );
            case 2:
                return (
                    <ThemeStep
                        formData={formData}
                        brands={brands || []}
                        errors={errors}
                        onChange={updateFormData}
                    />
                );
            default:
                return null;
        }
    };

    if (surveysLoading || brandsLoading) {
        return (
            <Content style={{ margin: '0 16px' }}>
                <div style={{ padding: 24, textAlign: 'center' }}>
                    <Spin size="large" />
                </div>
            </Content>
        );
    }

    if (!survey) {
        return (
            <Content style={{ margin: '0 16px' }}>
                <div style={{ padding: 24 }}>
                    <Alert 
                        type="error" 
                        message={intl.formatMessage({ id: 'survey.edit.notFound' })}
                        description={intl.formatMessage({ id: 'survey.edit.notFoundDesc' })}
                        action={
                            <Button onClick={() => navigate('/dashboard/surveys')}>
                                {intl.formatMessage({ id: 'survey.edit.backToSurveys' })}
                            </Button>
                        }
                    />
                </div>
            </Content>
        );
    }

    return (
        <Content style={{ margin: '0 16px' }}>
            <Breadcrumb 
                style={{ margin: '16px 0' }}
                items={[
                    { title: user?.organisation?.name || intl.formatMessage({ id: 'common.platformName' }) },
                    { 
                        title: (
                            <Button type="link" onClick={() => navigate('/dashboard/surveys')} style={{ padding: 0, height: 'auto', lineHeight: 'inherit', verticalAlign: 'baseline' }}>
                                {intl.formatMessage({ id: 'section.surveys' })}
                            </Button>
                        )
                    },
                    { 
                        title: (
                            <Button type="link" onClick={() => navigate(`/dashboard/surveys/${surveyHash}`)} style={{ padding: 0, height: 'auto', lineHeight: 'inherit', verticalAlign: 'baseline' }}>
                                {survey.name}
                            </Button>
                        )
                    },
                    { title: intl.formatMessage({ id: 'survey.edit.title' }) }
                ]}
            />

            <div
                style={{
                    padding: 24,
                    minHeight: '82vh',
                    background: colorBgContainer,
                    borderRadius: borderRadiusLG,
                }}
            >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                    <div>
                        <h2>{intl.formatMessage({ id: 'survey.edit.title' })}</h2>
                        <p style={{ color: '#6b7280', margin: 0 }}>
                            {intl.formatMessage({ id: 'survey.edit.subtitle' }, { name: survey.name })}
                        </p>
                    </div>
                    <Button
                        icon={<ArrowLeftOutlined />}
                        onClick={() => navigate(`/dashboard/surveys/${surveyHash}`)}
                    >
                        {intl.formatMessage({ id: 'common.cancelButton' })}
                    </Button>
                </div>

                <Card>
                    <Steps current={currentStep} items={steps} style={{ marginBottom: 32 }} />
                    
                    <div style={{ minHeight: '400px' }}>
                        {renderStepContent()}
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 32 }}>
                        <div>
                            {currentStep > 0 && (
                                <Button onClick={handlePrevious}>
                                    {intl.formatMessage({ id: 'common.previousButton' })}
                                </Button>
                            )}
                        </div>
                        <div>
                            {currentStep < steps.length - 1 ? (
                                <Button type="primary" onClick={handleNext}>
                                    {intl.formatMessage({ id: 'common.nextButton' })}
                                </Button>
                            ) : (
                                <Button 
                                    type="primary" 
                                    icon={<SaveOutlined />}
                                    loading={isSubmitting}
                                    onClick={handleSubmit}
                                >
                                    {intl.formatMessage({ id: 'survey.edit.submit' })}
                                </Button>
                            )}
                        </div>
                    </div>
                </Card>
            </div>
        </Content>
    );
};

export default EditSurveyPage;